import { get } from "lodash"
import { formatBetaInstrumentTooltip } from "helpers/contributors"
import { sanitizePositionSet, getBetaInstrumentFromOptions } from "../helpers"

const getFactorBetaQueryBody = (options, shouldIncludeCustomBetaData, shouldUseIntradayQuery) => {
  const { betaFields } = options
  const innerQuery = `
    betaData: beta {
      ${betaFields}
    }
    ${shouldIncludeCustomBetaData ? `customBetaData: beta (instrument: $instrument) {
      predicted
    }` : ""}
  `
  if (shouldUseIntradayQuery) {
    return `
    query ($modelId: String!, $batchId: Id, $positionSet: PositionSetInput!, $on: Date!) {
      model (id: $modelId) {
        simulation:intradaySimulationBatch (batchId: $batchId, positionSet: $positionSet, on: $on) {
          ${innerQuery}
        }
      }
    }`
  }

  return `
  query ($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $date: Date!, ${shouldIncludeCustomBetaData ? "$instrument: PositionSetInput" : ""}) {
    model (id: $modelId) {
      simulation (positionSet: $positionSet, base: $base, from: $date, to: $date) {
        ${innerQuery}
      }
    }
  }`
}

export function createFactorBetaQuery({ prefix, modelId, positionSet, base, to, ...options }) {
  const sanitizedPositionSet = sanitizePositionSet(positionSet)
  const betaInstrument = getBetaInstrumentFromOptions(options)
  const { on, batchId, isIntraday, isOpPro, shouldUseIntradayQuery } = options
  const shouldIncludeCustomBetaData = betaInstrument && !isOpPro && !isIntraday
  const query = getFactorBetaQueryBody(options, shouldIncludeCustomBetaData, shouldUseIntradayQuery)

  const additionalVariables = shouldUseIntradayQuery ? {
    on,
    batchId,
  } : {
    base,
    date: to,
    instrument: betaInstrument,
  }

  return {
    prefix,
    key: ["beta", "factors", prefix, modelId, positionSet, base, to, options.betaInstrument, on, batchId, shouldUseIntradayQuery],
    query,
    variables: {
      modelId,
      positionSet: sanitizedPositionSet,
      ...additionalVariables,
    },
    select: (result) => {
      const data = get(result, "data.model.simulation", {})
      const betaData = data.betaData?.at(-1)
      const customBetaData = data.customBetaData?.at(-1)
      const historicalBetaContribution = data.betaData.map(({ historical }) => ({ id: "beta", value: historical }))
      const predictedBetaContribution = data.betaData.map(({ predicted }) => ({ id: "beta", value: predicted }))
      const customBetaContribution = shouldIncludeCustomBetaData ? data.customBetaData.map(({ predicted }) => ({ id: "beta", value: predicted })) : []

      const children = {
        historical: {
          label: "Historical",
          tooltipValue: options.modelName ? `Historical beta values are provided by the ${options.modelName} model.` : null,
          values: {
            [prefix]: {
              value: betaData?.historical,
              contribution: historicalBetaContribution,
            },
          },
        },
        predicted: {
          label: "Predicted",
          tooltipValue: options.modelName ? `Predicted beta values are provided by the ${options.modelName} model.` : null,
          values: {
            [prefix]: {
              value: betaData?.predicted,
              contribution: predictedBetaContribution,
            },
          },
        },
      }

      if (shouldIncludeCustomBetaData) {
        const formattedBetaInstrumentTooltip = formatBetaInstrumentTooltip(options.betaInstrument)
        children.custom = {
          label: `Custom - ${get(formattedBetaInstrumentTooltip, "dynamicBetaId")}`,
          tooltipValue: formattedBetaInstrumentTooltip,
          values: {
            [prefix]: {
              value: customBetaData?.predicted,
              contribution: customBetaContribution,
            },
          },
        }
      }

      return {
        children,
      }
    },
  }
}
